import { useMemo } from "react";
import type { SWRConfiguration, SWRResponse } from "swr";

import {
    type ResponseStatus,
    type IModelMetadataResponse,
    type IModelValidationResponse,
    type IReturn
} from "src/api/Tmd.dtos";
import useApi from "src/hooks/api/useApi";
import useServiceStackClient from "src/hooks/api/useServiceStackClient";
import asyncDebounce from "src/utilities/asyncDebounce";

/**
 * Defines the response payload and status expected by this shared request adapter.
 */
export type TMDFormValidationResponse = IModelMetadataResponse & {
    responseStatus: ResponseStatus;
};

/**
 * Defines the typed request payload constructed by this shared adapter.
 */
export type TMDFormMetadataRequest<
    TResponse extends TMDFormValidationResponse,
    TModel,
    TMetadataContext
> = IReturn<TResponse> & {
    model: TModel;
    context: TMetadataContext;
};

const getData = <TResponse extends TMDFormValidationResponse>(
    response?: SWRResponse<TResponse, TResponse>
) => response?.data?.metadata;
const getError = <TResponse extends TMDFormValidationResponse>(
    response?: SWRResponse<TResponse, TResponse>
) => (response?.error ?? response?.data)?.responseStatus;
const getAdditionalState = <TResponse extends TMDFormValidationResponse>(
    _response?: SWRResponse<TResponse, TResponse>
) => ({});

/**
 * Loads model metadata through a memoized POST request and shared API state projection.
 *
 * Preconditions: Both model and context must be truthy to enable the request.
 *
 * Postconditions: Returns projected metadata/status state without altering the inputs; errors
 * take precedence over response status when projected.
 */
const useBaseFormMetadata = <
    TModel,
    TMetadataContext,
    TResponse extends TMDFormValidationResponse,
    TRequest extends TMDFormMetadataRequest<TResponse, TModel, TMetadataContext>
>(
    constructor: new (init?: Partial<TRequest>) => TRequest,
    model?: TModel | null | undefined,
    context?: TMetadataContext | null | undefined,
    configuration?: SWRConfiguration<TResponse, TResponse> | null | undefined
) => {
    const getRequest = useMemo(
        () => {
            if (!model || !context) {
                return undefined;
            }

            return (): IReturn<TResponse> =>
                new constructor({
                    model,
                    context
                } as Partial<TRequest>);
        },
        [model, context, constructor]
    );

    return useApi("post", getRequest, { getData, getError, getAdditionalState }, configuration);
};

export default useBaseFormMetadata;
